import React, { useEffect, useMemo, lazy, Suspense } from 'react';
import {
  Text,
  ChakraProvider,
  Container,
  Flex,
  Heading,
  Image,
  HStack,
  Button,
  Box,
  Link as ChakraLink,
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import Resizer from './modules/imageResizer/resizer';
import AppIconMaker from './modules/appIconMaker/appIconMaker';
import QrCode from './modules/qrCodeGenerator/qrCode';
import TextEditor from './modules/texteditor/textEditor';
import FocusLounge from './modules/focusLounge/focusLounge';
import TicTacToe from './modules/ticTacToe/ticTacToe';
import Calculator from './modules/calculator/calculator';
import TextCleaner from './modules/textCleaner/textCleaner';
import ImageMarker from './modules/screenshotAnnotator/screenshotAnnotator';
import PasswordGenerator from './modules/passwordGenerator/passwordGenerator';
import StreamPlayer from './modules/streamPlayer/StreamPlayer';
import PdfStudio from './modules/pdfStudio/pdfStudio';
import logo from './assets/images/Logo.png';
import bmcIcon from './assets/images/bmc-icon.png';
import { initGA, sendPageView, sendEvent } from './common/analytics';
import NotFound from './common/NotFound';
import LandingPage from './common/LandingPage';
import { BMC_LINK } from './common/config';
import AppLauncher from './common/appLauncher/AppLauncher';
import { useFavoriteApps } from './common/appLauncher/useFavoriteApps';
import { getToolIconTheme } from './common/appLauncher/toolIconTheme';
import { BRAND_WORDMARK_VORA, BRAND_WORDMARK_KIT_SX } from './common/brandWordmark';
import { getFooterToolGroups } from './common/appLauncher/appRegistry';
import {
  recordMainLogoClickForStreamPlayer,
  useStreamPlayerUnlocked,
} from './common/streamPlayerUnlock';

const FlowSpace = lazy(() => import('./modules/flowSpace/FlowSpace'));

const StreamPlayerRoute = () => {
  const unlocked = useStreamPlayerUnlocked();
  if (!unlocked) return <Navigate to="/" replace />;
  return <StreamPlayer />;
};

const App = () => {

  const location = useLocation();
  const favorites = useFavoriteApps();
  const streamPlayerUnlocked = useStreamPlayerUnlocked();
  const footerToolGroups = useMemo(
    () => getFooterToolGroups(streamPlayerUnlocked),
    [streamPlayerUnlocked],
  );

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    const PAGE_META = {
      '/':                'VoraKit - Free Browser Tools',
      '/imageresizer':    'VoraKit - Image Resizer',
      '/appiconmaker':    'VoraKit - App Icon Maker',
      '/qrcodegenerator': 'VoraKit - QR Code Generator',
      '/texteditor':      'VoraKit - Text Editor',
      '/focuslounge':     'VoraKit - Focus Lounge',
      '/tictactoe':       'VoraKit - Tic Tac Toe',
      '/calculator':      'VoraKit - Calculator',
      '/text-cleaner':    'VoraKit - Text Cleaner',
      '/image-marker': 'VoraKit - Image Marker',
      '/password-generator': 'VoraKit - Password Generator',
      '/stream-player': 'VoraKit - Stream Player',
      '/pdf-studio': 'VoraKit - PDF Studio',
      '/flowspace': 'VoraKit - FlowSpace',
    };
    const PAGE_DESCRIPTION = {
      '/': 'Free browser tools for images, text, PDFs, and more — no account, runs locally.',
      '/flowspace':
        'A simple drag-and-drop canvas for floor plans, furniture layouts, images, and flowcharts. Plan rooms, layouts, and flows visually.',
    };
    const title = PAGE_META[location.pathname] ?? 'VoraKit - Page Not Found';
    document.title = title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      'content',
      PAGE_DESCRIPTION[location.pathname] ?? 'VoraKit — free browser tools. No account, no upload, no tracking.',
    );
    // Fire a GA4 page-view with the human-readable title so
    // Reports > Engagement > Pages and screens shows module names.
    sendPageView(location.pathname, title);
  }, [location.pathname]);

  return (
    <ChakraProvider>
        <Container maxW="container.xl" p={5}>
          {/* ── Navbar ── */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            zIndex={999}
            bg={location.pathname === '/focuslounge' ? 'white' : 'rgba(255,255,255,0.85)'}
            backdropFilter={location.pathname === '/focuslounge' ? 'none' : 'blur(12px)'}
            borderBottomWidth="1px"
            borderColor="gray.100"
          >
            <Flex align="center" gap={3} w="100%" px={{ base: 4, md: 6 }} h="64px" minW={0}>
              {/* Logo + wordmark — left */}
              <Link to="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
                <HStack spacing={2.5}>
                  <Image
                    src={logo}
                    h="56px"
                    w="auto"
                    maxW="260px"
                    objectFit="contain"
                    alt="VoraKit"
                    onClick={() => recordMainLogoClickForStreamPlayer()}
                    cursor="pointer"
                  />
                  <Heading as="span" fontSize="2xl" letterSpacing="-0.3px" lineHeight={1} fontFamily="'Inter', sans-serif">
                    <Text {...BRAND_WORDMARK_VORA}>Vora</Text>
                    <Text as="span" sx={BRAND_WORDMARK_KIT_SX}>
                      Kit
                    </Text>
                  </Heading>
                </HStack>
              </Link>

              {/* Consume remaining width so favorites / donate / launcher stay viewport-right */}
              <Box flex="1" minW={0} />

              <HStack
                spacing={{ base: 1, md: 2 }}
                flexShrink={0}
                align="center"
                justify="flex-end"
              >
                {/* Favorite apps from launcher (up to 4) — full row visible, no horizontal scroll */}
                <HStack
                  spacing={{ md: 1, lg: 1.5 }}
                  display={{ base: 'none', md: 'flex' }}
                  flexWrap="nowrap"
                  flexShrink={1}
                  minW={0}
                  overflow="visible"
                >
                  {favorites.favoriteApps.map((app) => {
                    const { id, path, label, Icon } = app;
                    const isActive = location.pathname === path;
                    const iconTheme = getToolIconTheme(id);
                    return (
                      <ChakraLink
                        key={id}
                        as={Link}
                        to={path}
                        flexShrink={0}
                        display="inline-flex"
                        alignItems="center"
                        gap={1.5}
                        px={{ md: 1.5, lg: 2 }}
                        py={2}
                        borderRadius="full"
                        fontSize="sm"
                        fontWeight={isActive ? '600' : '500'}
                        color={isActive ? 'teal.700' : 'gray.600'}
                        bg={isActive ? 'teal.50' : 'transparent'}
                        _hover={{ bg: 'gray.50', color: 'gray.900', textDecoration: 'none' }}
                        transition="background 0.15s ease, color 0.15s ease"
                      >
                        <Box
                          as="span"
                          display="inline-flex"
                          lineHeight={0}
                          flexShrink={0}
                          color={iconTheme.iconColor}
                          aria-hidden
                        >
                          <Icon size={20} />
                        </Box>
                        <Text as="span" fontSize="sm" fontWeight="inherit">
                          {label}
                        </Text>
                      </ChakraLink>
                    );
                  })}
                </HStack>

                <Button
                  as={ChakraLink}
                  href={BMC_LINK}
                  size="sm"
                  borderRadius="lg"
                  colorScheme="yellow"
                  variant="solid"
                  isExternal
                  fontWeight="600"
                  flexShrink={0}
                  px={{ base: 2.5, md: 4 }}
                  onClick={() => sendEvent('MainMenu', 'Donate', 'clicked')}
                  _hover={{ transform: 'translateY(-1px)', shadow: 'md', textDecoration: 'none' }}
                  transition="all 0.15s"
                >
                  <Image src={bmcIcon} boxSize="15px" mr={{ base: 0, sm: 1.5 }} alt="" />
                  <Text as="span" display={{ base: 'none', sm: 'inline' }}>
                    Keep This Free
                  </Text>
                </Button>

                <Box flexShrink={0}>
                  <AppLauncher
                    ids={favorites.visibleFavoriteIds}
                    streamPlayerUnlocked={streamPlayerUnlocked}
                    favoriteApps={favorites.favoriteApps}
                    favoriteIdSet={favorites.favoriteIdSet}
                    isFull={favorites.isFull}
                    reorder={favorites.reorder}
                    addToFavorite={favorites.addToFavorite}
                    removeFromFavorite={favorites.removeFromFavorite}
                  />
                </Box>
              </HStack>
            </Flex>
          </Box>

          {/* Navbar height offset */}
          <Box h="64px" />

          {/* Content */}
          <Box pt={4}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/imageresizer" element={<Resizer />} />
              <Route path="/appiconmaker" element={<AppIconMaker />} />
              <Route path="/qrcodegenerator" element={<QrCode />} />
              <Route path="/texteditor" element={<TextEditor />} />
              <Route path="/focuslounge" element={<FocusLounge />} />
              <Route path="/tictactoe" element={<TicTacToe />} />
              <Route path="/calculator" element={<Calculator />} />
              <Route path="/text-cleaner" element={<TextCleaner />} />
              <Route path="/image-marker" element={<ImageMarker />} />
              <Route path="/password-generator" element={<PasswordGenerator />} />
              <Route path="/stream-player" element={<StreamPlayerRoute />} />
              <Route path="/pdf-studio" element={<PdfStudio />} />
              <Route path="/floor-planner" element={<Navigate to="/flowspace" replace />} />
              <Route
                path="/flowspace"
                element={
                  <Suspense
                    fallback={
                      <Box py={12} textAlign="center" fontSize="sm" color="gray.500">
                        Loading FlowSpace…
                      </Box>
                    }
                  >
                    <FlowSpace />
                  </Suspense>
                }
              />
              <Route path="/screenshot-annotator" element={<Navigate to="/image-marker" replace />} />
              {/* Catch-all: old bookmarks or unknown paths */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Box>

          {/* Footer — hidden on immersive tools */}
          {location.pathname !== '/focuslounge' &&
           location.pathname !== '/stream-player' &&
           location.pathname !== '/flowspace' && <Box
            mt={12}
            pt={8}
            pb={6}
            borderTopWidth="1px"
            borderColor="gray.100"
          >
            <Flex
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'center', md: 'flex-start' }}
              justify="space-between"
              gap={6}
            >
              {/* Brand + donate */}
              <Box textAlign={{ base: 'center', md: 'left' }}>
                <Heading as="span" fontSize="md" letterSpacing="-0.3px" fontFamily="'Inter', sans-serif">
                  <Text {...BRAND_WORDMARK_VORA}>Vora</Text>
                  <Text as="span" sx={BRAND_WORDMARK_KIT_SX}>
                    Kit
                  </Text>
                </Heading>
                <Text fontSize="xs" color="gray.400" mt={1} maxW="260px">
                  Free browser tools. No account, no upload, no tracking.
                </Text>
                <Box mt={3}>
                  <Text fontSize="sm" fontWeight="600" color="gray.600">Help us keep our tools free!</Text>
                  <Button
                    as={ChakraLink}
                    href={BMC_LINK}
                    colorScheme="yellow"
                    isExternal
                    size="xs"
                    borderRadius="md"
                    fontWeight="600"
                    mt={2}
                    onClick={() => sendEvent('Footer', 'Donate', 'clicked')}
                    _hover={{ transform: 'translateY(-1px)', shadow: 'sm', textDecoration: 'none' }}
                    transition="all 0.15s"
                  >
                    <Image src={bmcIcon} boxSize="12px" mr={1} alt="" />
                    Keep This Free
                  </Button>
                </Box>
              </Box>

              {/* Tools links — grouped by category */}
              <SimpleGrid
                columns={{ base: 2, sm: 3, md: 5 }}
                spacingX={{ base: 6, md: 10 }}
                spacingY={5}
                maxW={{ md: '900px' }}
                mx={{ base: 'auto', md: 0 }}
              >
                {footerToolGroups.map((group) => (
                  <VStack
                    key={group.title}
                    align={{ base: 'center', md: 'flex-start' }}
                    spacing={2}
                  >
                    <Text
                      fontSize="10px"
                      fontWeight="700"
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                    >
                      {group.title}
                    </Text>
                    <VStack align={{ base: 'center', md: 'flex-start' }} spacing={1.5}>
                      {group.items.map((item) => (
                        <ChakraLink
                          key={item.to}
                          as={Link}
                          to={item.to}
                          fontSize="sm"
                          color="gray.600"
                          fontWeight="500"
                          whiteSpace={item.to === '/password-generator' ? 'nowrap' : undefined}
                          _hover={{ color: 'blue.500', textDecoration: 'none' }}
                        >
                          {item.label}
                        </ChakraLink>
                      ))}
                    </VStack>
                  </VStack>
                ))}
              </SimpleGrid>
            </Flex>

            {/* Disclaimer + copyright */}
            <Box mt={6} pt={5} borderTopWidth="1px" borderColor="gray.100">
              <Text fontSize="xs" color="gray.400" textAlign="center" lineHeight="tall">
                <Text as="span" fontWeight="600" color="gray.500">Disclaimer: </Text>
                All files are processed locally in your browser and are never sent to nor stored on our servers.
                Browser storage may be cleared by your browser at any time.
                Usage is monitored solely for internal analytics purposes.
              </Text>
              <Text fontSize="xs" color="gray.400" textAlign="center" mt={2}>
                &copy; {currentYear} IPE Solutions LLC. All rights reserved.
              </Text>
            </Box>
          </Box>}
        </Container>
      
    </ChakraProvider>
  );
};

export default App;
